"use client";

import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { Connection } from "@/lib/hooks/use-connections";
import {
  Loader2,
  Save,
  Eye,
  EyeOff,
  Plus,
  X,
  Settings2,
  KeyRound,
  Globe,
  ChevronRight,
  Braces,
  FileJson,
  Upload,
  Check,
  AlertCircle,
  Trash2,
  RefreshCw,
  ListRestart,
} from "lucide-react";
import { getAuthToken } from "@/lib/auth";
import { API_ENDPOINTS } from "@/lib/api-config";
import { cn } from "@/lib/utils";

interface EditConnectionDialogProps {
  connection: Connection | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdated?: () => void;
}

// ── Auth field definitions per auth_type ──
interface FieldDef {
  key: string;
  label: string;
  placeholder: string;
  sensitive?: boolean;
  required?: boolean;
  source: "auth" | "config";
  mono?: boolean;
  hint?: string;
}

const AUTH_FIELDS: Record<string, FieldDef[]> = {
  api_key: [
    {
      key: "api_key",
      label: "API Key",
      placeholder: "sk-...",
      sensitive: true,
      required: true,
      source: "auth",
      mono: true,
    },
    {
      key: "api_key_name",
      label: "Header Name",
      placeholder: "X-API-Key",
      source: "config",
      hint: "Header or query param name",
    },
    {
      key: "api_key_location",
      label: "Key Location",
      placeholder: "header",
      source: "config",
      hint: '"header" or "query"',
    },
  ],
  bearer_token: [
    {
      key: "token",
      label: "Token",
      placeholder: "eyJhbGci...",
      sensitive: true,
      required: true,
      source: "auth",
      mono: true,
    },
    {
      key: "token_prefix",
      label: "Token Prefix",
      placeholder: "Bearer",
      source: "config",
      hint: 'Defaults to "Bearer"',
    },
  ],
  basic_auth: [
    {
      key: "username",
      label: "Username",
      placeholder: "user@company.com",
      source: "auth",
    },
    {
      key: "password",
      label: "Password",
      placeholder: "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022",
      sensitive: true,
      source: "auth",
    },
    {
      key: "encoded_token",
      label: "Encoded Token (alternative)",
      placeholder: "Base64 encoded token",
      sensitive: true,
      source: "auth",
      hint: "Use instead of username/password",
    },
  ],
  oauth2_client: [
    {
      key: "client_id",
      label: "Client ID",
      placeholder: "abc123...",
      required: true,
      source: "auth",
      mono: true,
    },
    {
      key: "client_secret",
      label: "Client Secret",
      placeholder: "secret...",
      sensitive: true,
      required: true,
      source: "auth",
      mono: true,
    },
    {
      key: "token_url",
      label: "Token URL",
      placeholder: "https://auth.example.com/oauth/token",
      required: true,
      source: "auth",
      mono: true,
    },
    {
      key: "scope",
      label: "Scope",
      placeholder: "https://graph.microsoft.com/.default",
      source: "auth",
      hint: "OAuth scope (e.g. https://resource/.default)",
    },
  ],
  oauth2_code: [
    {
      key: "client_id",
      label: "Client ID",
      placeholder: "abc123...",
      required: true,
      source: "auth",
      mono: true,
    },
    {
      key: "client_secret",
      label: "Client Secret",
      placeholder: "secret...",
      sensitive: true,
      source: "auth",
      mono: true,
    },
    {
      key: "access_token",
      label: "Access Token",
      placeholder: "eyJhbGci...",
      sensitive: true,
      source: "auth",
      mono: true,
    },
    {
      key: "refresh_token",
      label: "Refresh Token",
      placeholder: "rt_...",
      sensitive: true,
      source: "auth",
      mono: true,
    },
    {
      key: "token_url",
      label: "Token URL",
      placeholder: "https://auth.example.com/oauth/token",
      source: "auth",
      mono: true,
    },
    {
      key: "scope",
      label: "Scope",
      placeholder: "https://resource/.default",
      source: "auth",
      hint: "OAuth scope",
    },
  ],
  oauth2_password: [
    {
      key: "client_id",
      label: "Client ID",
      placeholder: "abc123...",
      required: true,
      source: "auth",
      mono: true,
    },
    {
      key: "client_secret",
      label: "Client Secret",
      placeholder: "secret...",
      sensitive: true,
      source: "auth",
      mono: true,
    },
    {
      key: "username",
      label: "Username",
      placeholder: "user@company.com",
      required: true,
      source: "auth",
    },
    {
      key: "password",
      label: "Password",
      placeholder: "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022",
      sensitive: true,
      required: true,
      source: "auth",
    },
    {
      key: "token_url",
      label: "Token URL",
      placeholder: "https://auth.example.com/oauth/token",
      required: true,
      source: "auth",
      mono: true,
    },
    {
      key: "scope",
      label: "Scope",
      placeholder: "https://resource/.default",
      source: "auth",
      hint: "OAuth scope",
    },
  ],
  jwt: [
    {
      key: "service_account_json",
      label: "Service Account JSON",
      placeholder: '{"type":"service_account",...}',
      sensitive: true,
      required: true,
      source: "auth",
      mono: true,
    },
    {
      key: "scope",
      label: "Scope",
      placeholder: "https://www.googleapis.com/auth/...",
      source: "config",
      mono: true,
    },
  ],
  aws_signature: [
    {
      key: "access_key_id",
      label: "Access Key ID",
      placeholder: "AKIA...",
      required: true,
      source: "auth",
      mono: true,
    },
    {
      key: "secret_access_key",
      label: "Secret Access Key",
      placeholder: "wJalr...",
      sensitive: true,
      required: true,
      source: "auth",
      mono: true,
    },
    {
      key: "region",
      label: "AWS Region",
      placeholder: "us-east-1",
      required: true,
      source: "config",
    },
    {
      key: "service",
      label: "Service",
      placeholder: "execute-api",
      source: "config",
    },
  ],
  custom_header: [
    {
      key: "header_name",
      label: "Header Name",
      placeholder: "X-Custom-Auth",
      required: true,
      source: "auth",
    },
    {
      key: "header_value",
      label: "Header Value",
      placeholder: "custom-value",
      sensitive: true,
      required: true,
      source: "auth",
    },
  ],
  none: [],
};

interface HeaderPair {
  key: string;
  value: string;
}

// ── Input shared class ──
const inputBase =
  "h-9 w-full rounded-lg border border-white/[0.06] bg-white/[0.025] px-3 text-[13px] text-white/80 placeholder:text-white/25 outline-none ring-0 transition-all duration-200 focus:border-teal-500/30 focus:bg-white/[0.04] focus:shadow-[0_0_0_3px_rgba(45,212,191,0.06)]";
const inputMono = "font-mono text-[12px] tracking-tight";

// ── Sensitive input ──
function SensitiveInput({
  value,
  onChange,
  placeholder,
  mono,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  mono?: boolean;
}) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative group/sens">
      <input
        type={visible ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={cn(inputBase, "pr-9", mono && inputMono)}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-white/35 transition-colors duration-150 hover:text-white/45"
        tabIndex={-1}
      >
        {visible ? (
          <EyeOff className="h-3.5 w-3.5" />
        ) : (
          <Eye className="h-3.5 w-3.5" />
        )}
      </button>
    </div>
  );
}

// ── Section ──
function Section({
  icon: Icon,
  title,
  accentColor = "bg-teal-400",
  accentShadow = "shadow-[0_0_6px_rgba(45,212,191,0.5)]",
  defaultOpen = true,
  children,
}: {
  icon: React.ElementType;
  title: string;
  accentColor?: string;
  accentShadow?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="group/section">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2.5 py-2 text-left"
      >
        <div
          className={cn("h-1.5 w-1.5 rounded-full", accentColor, accentShadow)}
        />
        <Icon className="h-3 w-3 text-white/40" />
        <span className="flex-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/50">
          {title}
        </span>
        <ChevronRight
          className={cn(
            "h-3 w-3 text-white/35 transition-transform duration-200",
            open && "rotate-90",
          )}
        />
      </button>
      <div
        className={cn(
          "grid transition-all duration-200",
          open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
        )}
      >
        <div className="overflow-hidden">
          <div className="space-y-3 rounded-xl border border-white/[0.04] bg-white/[0.015] p-4 mt-1">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Label ──
function FieldLabel({
  label,
  required,
  hint,
}: {
  label: string;
  required?: boolean;
  hint?: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <label className="text-[13px] font-medium text-white/50">
        {label}
        {required && <span className="ml-0.5 text-teal-400/50">*</span>}
      </label>
      {hint && <span className="text-[10px] text-white/35">{hint}</span>}
    </div>
  );
}

// ── Auth type labels ──
const authTypeLabels: Record<string, string> = {
  none: "None",
  api_key: "API Key",
  bearer_token: "Bearer Token",
  basic_auth: "Basic Auth",
  oauth2_client: "OAuth2 Client Credentials",
  oauth2_code: "OAuth2 Authorization Code",
  oauth2_password: "OAuth2 Password",
  oauth2_implicit: "OAuth2 Implicit",
  jwt: "JWT / Service Account",
  aws_signature: "AWS Signature v4",
  custom_header: "Custom Header",
  certificate: "Certificate",
  ssh_key: "SSH Key",
};

export function EditConnectionDialog({
  connection,
  open,
  onOpenChange,
  onUpdated,
}: EditConnectionDialogProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  const [testEndpoint, setTestEndpoint] = useState("");
  const [timeout, setTimeout_] = useState("");
  const [authFields, setAuthFields] = useState<Record<string, string>>({});
  const [configFields, setConfigFields] = useState<Record<string, string>>({});
  const [headers, setHeaders] = useState<HeaderPair[]>([]);

  // Pagination state
  const [pgStrategy, setPgStrategy] = useState("none");
  const [pgPageSize, setPgPageSize] = useState("100");
  const [pgMaxPages, setPgMaxPages] = useState("50");
  const [pgMaxRecords, setPgMaxRecords] = useState("5000");
  const [pgAdvanced, setPgAdvanced] = useState(false);
  const [pgOffsetParam, setPgOffsetParam] = useState("offset");
  const [pgLimitParam, setPgLimitParam] = useState("limit");
  const [pgPageParam, setPgPageParam] = useState("page");
  const [pgSizeParam, setPgSizeParam] = useState("pageSize");
  const [pgCursorParam, setPgCursorParam] = useState("cursor");
  const [pgDataPath, setPgDataPath] = useState("");
  const [pgTotalPath, setPgTotalPath] = useState("");
  const [pgNextCursorPath, setPgNextCursorPath] = useState("");
  const [pgNextUrlPath, setPgNextUrlPath] = useState("");
  const [pgHasMorePath, setPgHasMorePath] = useState("");

  useEffect(() => {
    if (connection && open) {
      setName(connection.name || "");
      setDescription(connection.description || "");
      setBaseUrl(connection.config?.base_url || "");
      setTestEndpoint(connection.config?.test_endpoint || "");
      setTimeout_(String(connection.config?.timeout || ""));
      setError(null);

      const auth = connection.auth_config || {};
      const cfg = connection.config || {};
      const newAuth: Record<string, string> = {};
      const fields = AUTH_FIELDS[connection.auth_type] || [];
      for (const f of fields) {
        if (f.source === "auth") {
          newAuth[f.key] = String(auth[f.key] ?? cfg[f.key] ?? "");
        }
      }
      setAuthFields(newAuth);

      const newCfg: Record<string, string> = {};
      for (const f of fields) {
        if (f.source === "config") {
          newCfg[f.key] = String(cfg[f.key] ?? auth[f.key] ?? "");
        }
      }
      setConfigFields(newCfg);

      const existingHeaders = cfg.headers || cfg.custom_headers || {};
      const pairs: HeaderPair[] = Object.entries(existingHeaders).map(
        ([k, v]) => ({
          key: k,
          value: String(v),
        }),
      );
      setHeaders(pairs.length > 0 ? pairs : []);

      // Pagination config
      const pg = cfg.pagination || {};
      setPgStrategy(pg.strategy || "auto");
      setPgPageSize(String(pg.page_size ?? "100"));
      setPgMaxPages(String(pg.max_pages ?? "50"));
      setPgMaxRecords(String(pg.max_records ?? "5000"));
      const pgParams = pg.params || {};
      setPgOffsetParam(pgParams.offset_param || "offset");
      setPgLimitParam(pgParams.limit_param || "limit");
      setPgPageParam(pgParams.page_param || "page");
      setPgSizeParam(pgParams.size_param || "pageSize");
      setPgCursorParam(pgParams.cursor_param || "cursor");
      const pgPaths = pg.response_paths || {};
      setPgDataPath(pgPaths.data_path || "");
      setPgTotalPath(pgPaths.total_path || "");
      setPgNextCursorPath(pgPaths.next_cursor_path || "");
      setPgNextUrlPath(pgPaths.next_url_path || "");
      setPgHasMorePath(pgPaths.has_more_path || "");
      setPgAdvanced(false);

      // Reset spec state
      setSpecFile(null);
      setSpecError(null);
      setSpecSuccess(null);
    }
  }, [connection, open]);

  const updateAuthField = useCallback((key: string, value: string) => {
    setAuthFields((prev) => ({ ...prev, [key]: value }));
  }, []);

  const updateConfigField = useCallback((key: string, value: string) => {
    setConfigFields((prev) => ({ ...prev, [key]: value }));
  }, []);

  const addHeader = () => setHeaders((h) => [...h, { key: "", value: "" }]);
  const removeHeader = (i: number) =>
    setHeaders((h) => h.filter((_, idx) => idx !== i));
  const updateHeader = (i: number, field: "key" | "value", value: string) =>
    setHeaders((h) =>
      h.map((pair, idx) => (idx === i ? { ...pair, [field]: value } : pair)),
    );

  // ── OpenAPI spec state ──
  const [specFile, setSpecFile] = useState<File | null>(null);
  const [isUploadingSpec, setIsUploadingSpec] = useState(false);
  const [isClearingSpec, setIsClearingSpec] = useState(false);
  const [specError, setSpecError] = useState<string | null>(null);
  const [specSuccess, setSpecSuccess] = useState<string | null>(null);
  const specFileInputRef = useCallback((node: HTMLInputElement | null) => {
    if (node) node.value = "";
  }, []);

  const hasExistingSpec = !!(
    connection?.config?.openapi_spec_uploaded ||
    connection?.config?.openapi_spec_title
  );
  const specTitle = connection?.config?.openapi_spec_title as string | undefined;
  const specVersion = connection?.config?.openapi_spec_version as string | undefined;
  const specEndpointCount = connection?.config?.openapi_endpoint_count as number | undefined;

  const handleSpecFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const name = file.name.toLowerCase();
    if (![".json", ".yaml", ".yml"].some((ext) => name.endsWith(ext))) {
      setSpecError("File must be .json, .yaml, or .yml");
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      setSpecError("File size must be under 50MB");
      return;
    }
    setSpecFile(file);
    setSpecError(null);
    setSpecSuccess(null);
  };

  const handleUploadSpec = async () => {
    if (!specFile || !connection) return;
    setIsUploadingSpec(true);
    setSpecError(null);
    setSpecSuccess(null);

    try {
      const token = getAuthToken();
      const API_URL = process.env.NEXT_PUBLIC_BACKEND_URL;

      // Step 1: If an existing spec is loaded, clear it first
      if (hasExistingSpec) {
        await fetch(`${API_URL}/v1/connections/${connection.id}/spec`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      // Step 2: Upload new spec with connection_id to trigger full pipeline
      const formDataUpload = new FormData();
      formDataUpload.append("file", specFile);

      const response = await fetch(
        API_ENDPOINTS.connectionUploadSpec(connection.id),
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formDataUpload,
        },
      );

      if (!response.ok) {
        const errData = await response.json().catch(() => null);
        let errMsg = `Upload failed (${response.status})`;
        if (errData?.detail) {
          errMsg =
            typeof errData.detail === "string"
              ? errData.detail
              : JSON.stringify(errData.detail);
        }
        throw new Error(errMsg);
      }

      const data = await response.json();
      const info = data.info;
      setSpecFile(null);
      setSpecSuccess(
        `Parsed "${info?.title || "Spec"}" — ${info?.endpoint_count ?? 0} endpoints. Processing in background.`,
      );
      onUpdated?.();
    } catch (err) {
      setSpecError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setIsUploadingSpec(false);
    }
  };

  const handleClearSpec = async () => {
    if (!connection) return;
    setIsClearingSpec(true);
    setSpecError(null);
    setSpecSuccess(null);

    try {
      const token = getAuthToken();
      const API_URL = process.env.NEXT_PUBLIC_BACKEND_URL;

      const response = await fetch(
        `${API_URL}/v1/connections/${connection.id}/spec`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (!response.ok) {
        const errData = await response.json().catch(() => null);
        throw new Error(errData?.detail || "Failed to clear spec");
      }

      const data = await response.json();
      setSpecSuccess(
        `Cleared ${data.endpoints_deleted ?? 0} endpoints, ${data.memories_deleted ?? 0} memories, ${data.chromadb_deleted ?? 0} embeddings.`,
      );
      setSpecFile(null);
      onUpdated?.();
    } catch (err) {
      setSpecError(err instanceof Error ? err.message : "Clear failed");
    } finally {
      setIsClearingSpec(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!connection) return;

    setLoading(true);
    setError(null);

    try {
      const newAuthConfig: Record<string, string> = {};
      for (const [k, v] of Object.entries(authFields)) {
        if (v) newAuthConfig[k] = v;
      }

      const newConfig: Record<string, unknown> = {
        ...connection.config,
        base_url: baseUrl,
      };
      for (const [k, v] of Object.entries(configFields)) {
        if (v) newConfig[k] = v;
        else delete newConfig[k];
      }
      if (testEndpoint) newConfig.test_endpoint = testEndpoint;
      else delete newConfig.test_endpoint;
      if (timeout && !isNaN(Number(timeout)))
        newConfig.timeout = Number(timeout);

      const headersObj: Record<string, string> = {};
      for (const h of headers) {
        if (h.key.trim()) headersObj[h.key.trim()] = h.value;
      }
      if (Object.keys(headersObj).length > 0) {
        newConfig.headers = headersObj;
      } else {
        delete newConfig.headers;
        delete newConfig.custom_headers;
      }

      // Build pagination config only when explicitly enabled
      if (isHttpConnection && pgStrategy !== "none") {
        const responsePaths: Record<string, string> = {};
        if (pgDataPath) responsePaths.data_path = pgDataPath;
        if (pgTotalPath) responsePaths.total_path = pgTotalPath;
        if (pgNextCursorPath) responsePaths.next_cursor_path = pgNextCursorPath;
        if (pgNextUrlPath) responsePaths.next_url_path = pgNextUrlPath;
        if (pgHasMorePath) responsePaths.has_more_path = pgHasMorePath;

        newConfig.pagination = {
          strategy: pgStrategy,
          page_size: Number(pgPageSize) || 100,
          max_pages: Number(pgMaxPages) || 50,
          max_records: Number(pgMaxRecords) || 5000,
          params: {
            offset_param: pgOffsetParam,
            limit_param: pgLimitParam,
            page_param: pgPageParam,
            size_param: pgSizeParam,
            cursor_param: pgCursorParam,
          },
          ...(Object.keys(responsePaths).length > 0 && { response_paths: responsePaths }),
        };
      } else if (isHttpConnection) {
        delete newConfig.pagination;
      }

      const token = getAuthToken();
      const API_URL = process.env.NEXT_PUBLIC_BACKEND_URL;
      const response = await fetch(
        `${API_URL}/v1/connections/${connection.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            name,
            description: description || undefined,
            config: newConfig,
            auth_config: newAuthConfig,
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to update connection");
      }

      onOpenChange(false);
      onUpdated?.();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to update connection",
      );
    } finally {
      setLoading(false);
    }
  };

  if (!connection) return null;

  const authType = connection.auth_type || "none";
  const fields = AUTH_FIELDS[authType] || [];
  const authFieldDefs = fields.filter((f) => f.source === "auth");
  const configFieldDefs = fields.filter((f) => f.source === "config");
  const isHttpConnection = ["rest_api", "graphql"].includes(connection.type);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex flex-col max-w-[680px] max-h-[88vh] overflow-hidden border-white/[0.06] bg-[#080c15] p-0 gap-0 shadow-2xl shadow-black/70"
        closeClassName="text-white/40 hover:text-white/50"
      >
        {/* Scoped scrollbar styles */}
        <style>{`
          .edit-conn-scroll::-webkit-scrollbar { width: 5px; }
          .edit-conn-scroll::-webkit-scrollbar-track { background: transparent; margin: 8px 0; }
          .edit-conn-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.06); border-radius: 10px; }
          .edit-conn-scroll::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.14); }
          .edit-conn-scroll { scrollbar-width: thin; scrollbar-color: rgba(255,255,255,0.06) transparent; }
          .edit-conn-scroll:hover { scrollbar-color: rgba(255,255,255,0.1) transparent; }
        `}</style>

        {/* Header with ambient glow */}
        <div className="relative overflow-hidden px-6 pt-5 pb-4 flex-shrink-0">
          <div className="pointer-events-none absolute -right-20 -top-20 h-40 w-40 rounded-full bg-teal-500/[0.04] blur-3xl" />
          <DialogHeader className="relative">
            <div className="flex items-center gap-2.5 mb-1.5">
              <div className="h-1.5 w-1.5 rounded-full bg-teal-400 shadow-[0_0_6px_rgba(45,212,191,0.5)]" />
              <DialogTitle className="text-[14px] font-semibold tracking-tight text-white/90">
                Edit Connection
              </DialogTitle>
            </div>
            <DialogDescription className="text-[12px] leading-relaxed text-white/60 pl-4">
              Update configuration, credentials, and headers. Changes apply
              immediately.
            </DialogDescription>
          </DialogHeader>

          {/* Connection identity pill */}
          <div className="mt-3 flex items-center gap-2">
            <span className="rounded-full border border-white/[0.06] bg-white/[0.02] px-2.5 py-0.5 text-[10px] font-medium capitalize text-white/60">
              {connection.type.replace("_", " ")}
            </span>
            <span className="text-white/35">&middot;</span>
            <span className="rounded-full border border-teal-500/10 bg-teal-500/[0.03] px-2.5 py-0.5 text-[10px] font-medium text-teal-400/50">
              {authTypeLabels[authType] || authType}
            </span>
          </div>
        </div>

        <div className="h-px flex-shrink-0 bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" />

        <form
          onSubmit={handleSubmit}
          className="flex flex-col overflow-hidden min-h-0 flex-1"
        >
          <div className="edit-conn-scroll overflow-y-auto px-6 py-4 space-y-4 min-h-0 flex-1">
            {/* ── General ── */}
            <Section icon={Settings2} title="General">
              <div className="space-y-1.5">
                <FieldLabel label="Name" required />
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Connection name"
                  required
                  className={inputBase}
                />
              </div>
              <div className="space-y-1.5">
                <FieldLabel label="Description" />
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Optional description"
                  rows={2}
                  className={cn(inputBase, "h-auto py-2 resize-none")}
                />
              </div>
            </Section>

            {/* ── Endpoint ── */}
            <Section icon={Globe} title="Endpoint">
              <div className="space-y-1.5">
                <FieldLabel label="Base URL" required />
                <input
                  value={baseUrl}
                  onChange={(e) => setBaseUrl(e.target.value)}
                  placeholder="https://api.example.com"
                  required
                  className={cn(inputBase, inputMono)}
                />
              </div>

              {configFieldDefs.map((f) => (
                <div key={f.key} className="space-y-1.5">
                  <FieldLabel
                    label={f.label}
                    required={f.required}
                    hint={f.hint}
                  />
                  <input
                    value={configFields[f.key] || ""}
                    onChange={(e) => updateConfigField(f.key, e.target.value)}
                    placeholder={f.placeholder}
                    className={cn(inputBase, f.mono && inputMono)}
                  />
                </div>
              ))}

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <FieldLabel label="Test Endpoint" hint="Validation path" />
                  <input
                    value={testEndpoint}
                    onChange={(e) => setTestEndpoint(e.target.value)}
                    placeholder="/health"
                    className={cn(inputBase, inputMono)}
                  />
                </div>
                <div className="space-y-1.5">
                  <FieldLabel label="Timeout" hint="Seconds" />
                  <input
                    value={timeout}
                    onChange={(e) => setTimeout_(e.target.value)}
                    placeholder="30"
                    type="number"
                    min={1}
                    max={300}
                    className={cn(inputBase, inputMono)}
                  />
                </div>
              </div>
            </Section>

            {/* ── OpenAPI Spec ── */}
            <Section
              icon={FileJson}
              title="OpenAPI Specification"
              accentColor="bg-sky-400"
              accentShadow="shadow-[0_0_6px_rgba(56,189,248,0.4)]"
              defaultOpen={hasExistingSpec}
            >
              {/* Existing spec info */}
              {hasExistingSpec && !specSuccess && (
                <div className="flex items-center justify-between rounded-lg border border-sky-500/15 bg-sky-500/[0.04] p-3">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <FileJson className="h-4 w-4 flex-shrink-0 text-sky-400/70" />
                    <div className="min-w-0">
                      <p className="truncate text-[12px] font-medium text-white/80">
                        {specTitle || "OpenAPI Spec"}
                        {specVersion && (
                          <span className="ml-1.5 text-white/40">v{specVersion}</span>
                        )}
                      </p>
                      {specEndpointCount != null && (
                        <p className="text-[10px] text-white/40">
                          {specEndpointCount} endpoints
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleClearSpec}
                    disabled={isClearingSpec}
                    className="flex h-7 items-center gap-1.5 rounded-md border border-rose-500/15 bg-rose-500/[0.04] px-2.5 text-[10px] font-medium text-rose-400/70 transition-all hover:bg-rose-500/10 hover:text-rose-400 disabled:opacity-40"
                  >
                    {isClearingSpec ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Trash2 className="h-3 w-3" />
                    )}
                    Clear
                  </button>
                </div>
              )}

              {/* Upload zone */}
              <div className="space-y-2.5">
                <div className="flex items-center gap-2">
                  <label
                    className={cn(
                      "flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed p-3 transition-all",
                      specFile
                        ? "border-sky-500/30 bg-sky-500/[0.04]"
                        : "border-white/[0.08] bg-white/[0.015] hover:border-white/15 hover:bg-white/[0.03]",
                    )}
                  >
                    <input
                      type="file"
                      accept=".json,.yaml,.yml"
                      className="hidden"
                      onChange={handleSpecFileChange}
                      ref={specFileInputRef}
                    />
                    {specFile ? (
                      <>
                        <FileJson className="h-4 w-4 text-sky-400" />
                        <span className="truncate text-[12px] text-white/70">
                          {specFile.name}
                        </span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            setSpecFile(null);
                            setSpecError(null);
                          }}
                          className="ml-auto text-white/35 hover:text-white/50"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 text-white/30" />
                        <span className="text-[13px] text-white/40">
                          {hasExistingSpec ? "Replace spec" : "Upload spec"}{" "}
                          <span className="text-white/25">(.json, .yaml)</span>
                        </span>
                      </>
                    )}
                  </label>
                </div>

                {specFile && (
                  <button
                    type="button"
                    onClick={handleUploadSpec}
                    disabled={isUploadingSpec}
                    className="flex w-full items-center justify-center gap-2 rounded-lg border border-sky-500/20 bg-sky-500/10 py-2 text-[13px] font-medium text-sky-300 transition-all hover:bg-sky-500/15 disabled:opacity-40"
                  >
                    {isUploadingSpec ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        {hasExistingSpec ? "Replacing..." : "Uploading..."}
                      </>
                    ) : (
                      <>
                        {hasExistingSpec ? (
                          <RefreshCw className="h-3.5 w-3.5" />
                        ) : (
                          <Upload className="h-3.5 w-3.5" />
                        )}
                        {hasExistingSpec
                          ? "Replace & Re-process"
                          : "Upload & Process"}
                      </>
                    )}
                  </button>
                )}
              </div>

              {/* Success message */}
              {specSuccess && (
                <div className="flex items-start gap-2 rounded-lg border border-emerald-500/15 bg-emerald-500/[0.04] p-3">
                  <Check className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-emerald-400" />
                  <p className="text-[13px] text-emerald-400/80">{specSuccess}</p>
                </div>
              )}

              {/* Error message */}
              {specError && (
                <div className="flex items-start gap-2 rounded-lg border border-rose-500/15 bg-rose-500/[0.04] p-3">
                  <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-rose-400" />
                  <p className="text-[13px] text-rose-400/80">{specError}</p>
                </div>
              )}
            </Section>

            {/* ── Authentication ── */}
            {authType !== "none" && authFieldDefs.length > 0 && (
              <Section
                icon={KeyRound}
                title="Authentication"
                accentColor="bg-amber-400"
                accentShadow="shadow-[0_0_6px_rgba(251,191,36,0.4)]"
              >
                {authFieldDefs.map((f) => (
                  <div key={f.key} className="space-y-1.5">
                    <FieldLabel
                      label={f.label}
                      required={f.required}
                      hint={f.hint}
                    />
                    {f.sensitive ? (
                      <SensitiveInput
                        value={authFields[f.key] || ""}
                        onChange={(v) => updateAuthField(f.key, v)}
                        placeholder={f.placeholder}
                        mono={f.mono}
                      />
                    ) : (
                      <input
                        value={authFields[f.key] || ""}
                        onChange={(e) => updateAuthField(f.key, e.target.value)}
                        placeholder={f.placeholder}
                        className={cn(inputBase, f.mono && inputMono)}
                      />
                    )}
                  </div>
                ))}
              </Section>
            )}

            {/* ── Custom Headers ── */}
            <Section
              icon={Braces}
              title="Custom Headers"
              accentColor="bg-violet-400"
              accentShadow="shadow-[0_0_6px_rgba(167,139,250,0.4)]"
              defaultOpen={headers.length > 0}
            >
              {headers.length === 0 && (
                <p className="text-[13px] text-white/25">
                  No custom headers configured. Add headers sent with every
                  request.
                </p>
              )}
              {headers.map((h, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    value={h.key}
                    onChange={(e) => updateHeader(i, "key", e.target.value)}
                    placeholder="Header-Name"
                    className={cn(
                      inputBase,
                      inputMono,
                      "h-8 w-[38%] px-2.5 text-[13px]",
                    )}
                  />
                  <input
                    value={h.value}
                    onChange={(e) => updateHeader(i, "value", e.target.value)}
                    placeholder="value"
                    className={cn(
                      inputBase,
                      inputMono,
                      "h-8 flex-1 px-2.5 text-[13px]",
                    )}
                  />
                  <button
                    type="button"
                    onClick={() => removeHeader(i)}
                    className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-white/35 transition-all duration-150 hover:bg-rose-500/8 hover:text-rose-400/60"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addHeader}
                className="flex items-center gap-1.5 rounded-lg border border-dashed border-white/[0.06] px-3 py-1.5 text-[10px] font-medium text-white/40 transition-all duration-200 hover:border-blue-500/20 hover:bg-blue-500/[0.03] hover:text-blue-300/50"
              >
                <Plus className="h-3 w-3" />
                Add Header
              </button>
            </Section>

            {/* ── Pagination (HTTP connections only) ── */}
            {isHttpConnection && (
              <Section
                icon={ListRestart}
                title="Pagination"
                accentColor="bg-cyan-400"
                accentShadow="shadow-[0_0_6px_rgba(34,211,238,0.4)]"
                defaultOpen={pgStrategy !== "auto" && pgStrategy !== "none"}
              >
                <div className="space-y-1.5">
                  <FieldLabel label="Strategy" hint="How the API paginates" />
                  <select
                    value={pgStrategy}
                    onChange={(e) => setPgStrategy(e.target.value)}
                    className={cn(inputBase, "appearance-none cursor-pointer")}
                  >
                    <option value="auto">Auto-detect</option>
                    <option value="offset_limit">Offset / Limit</option>
                    <option value="page_number">Page Number</option>
                    <option value="cursor">Cursor-based</option>
                    <option value="link_header">Link Header</option>
                    <option value="next_url">Next URL</option>
                    <option value="none">Disabled</option>
                  </select>
                </div>

                {pgStrategy !== "none" && (
                  <>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-1.5">
                        <FieldLabel label="Page Size" />
                        <input
                          type="number"
                          value={pgPageSize}
                          onChange={(e) => setPgPageSize(e.target.value)}
                          placeholder="100"
                          min={1}
                          max={10000}
                          className={cn(inputBase, inputMono)}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <FieldLabel label="Max Pages" />
                        <input
                          type="number"
                          value={pgMaxPages}
                          onChange={(e) => setPgMaxPages(e.target.value)}
                          placeholder="50"
                          min={1}
                          max={1000}
                          className={cn(inputBase, inputMono)}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <FieldLabel label="Max Records" />
                        <input
                          type="number"
                          value={pgMaxRecords}
                          onChange={(e) => setPgMaxRecords(e.target.value)}
                          placeholder="5000"
                          min={1}
                          max={100000}
                          className={cn(inputBase, inputMono)}
                        />
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => setPgAdvanced((v) => !v)}
                      className="flex items-center gap-1.5 text-[10px] font-medium text-white/40 transition-colors hover:text-white/55"
                    >
                      <ChevronRight
                        className={cn(
                          "h-3 w-3 transition-transform duration-200",
                          pgAdvanced && "rotate-90",
                        )}
                      />
                      Advanced parameter names
                    </button>

                    {pgAdvanced && (
                      <div className="space-y-3 rounded-lg border border-white/[0.04] bg-white/[0.01] p-3">
                        <p className="text-[10px] text-white/30">
                          Customize query parameter names and response field paths
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1.5">
                            <FieldLabel label="Offset param" />
                            <input
                              value={pgOffsetParam}
                              onChange={(e) => setPgOffsetParam(e.target.value)}
                              placeholder="offset"
                              className={cn(inputBase, inputMono, "h-8 text-[13px]")}
                            />
                          </div>
                          <div className="space-y-1.5">
                            <FieldLabel label="Limit param" />
                            <input
                              value={pgLimitParam}
                              onChange={(e) => setPgLimitParam(e.target.value)}
                              placeholder="limit"
                              className={cn(inputBase, inputMono, "h-8 text-[13px]")}
                            />
                          </div>
                          <div className="space-y-1.5">
                            <FieldLabel label="Page param" />
                            <input
                              value={pgPageParam}
                              onChange={(e) => setPgPageParam(e.target.value)}
                              placeholder="page"
                              className={cn(inputBase, inputMono, "h-8 text-[13px]")}
                            />
                          </div>
                          <div className="space-y-1.5">
                            <FieldLabel label="Page size param" />
                            <input
                              value={pgSizeParam}
                              onChange={(e) => setPgSizeParam(e.target.value)}
                              placeholder="pageSize"
                              className={cn(inputBase, inputMono, "h-8 text-[13px]")}
                            />
                          </div>
                          <div className="space-y-1.5">
                            <FieldLabel label="Cursor param" />
                            <input
                              value={pgCursorParam}
                              onChange={(e) => setPgCursorParam(e.target.value)}
                              placeholder="cursor"
                              className={cn(inputBase, inputMono, "h-8 text-[13px]")}
                            />
                          </div>
                        </div>

                        <div className="h-px bg-white/[0.04]" />
                        <p className="text-[10px] text-white/30">
                          Response field paths (dot-notation, e.g. meta.next_cursor)
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1.5">
                            <FieldLabel label="Data path" hint="Array field" />
                            <input
                              value={pgDataPath}
                              onChange={(e) => setPgDataPath(e.target.value)}
                              placeholder="data"
                              className={cn(inputBase, inputMono, "h-8 text-[13px]")}
                            />
                          </div>
                          <div className="space-y-1.5">
                            <FieldLabel label="Total path" hint="Total count" />
                            <input
                              value={pgTotalPath}
                              onChange={(e) => setPgTotalPath(e.target.value)}
                              placeholder="total"
                              className={cn(inputBase, inputMono, "h-8 text-[13px]")}
                            />
                          </div>
                          <div className="space-y-1.5">
                            <FieldLabel label="Next cursor path" />
                            <input
                              value={pgNextCursorPath}
                              onChange={(e) => setPgNextCursorPath(e.target.value)}
                              placeholder="meta.next_cursor"
                              className={cn(inputBase, inputMono, "h-8 text-[13px]")}
                            />
                          </div>
                          <div className="space-y-1.5">
                            <FieldLabel label="Next URL path" />
                            <input
                              value={pgNextUrlPath}
                              onChange={(e) => setPgNextUrlPath(e.target.value)}
                              placeholder="next"
                              className={cn(inputBase, inputMono, "h-8 text-[13px]")}
                            />
                          </div>
                          <div className="space-y-1.5">
                            <FieldLabel label="Has more path" />
                            <input
                              value={pgHasMorePath}
                              onChange={(e) => setPgHasMorePath(e.target.value)}
                              placeholder="has_more"
                              className={cn(inputBase, inputMono, "h-8 text-[13px]")}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </Section>
            )}

            {/* Error */}
            {error && (
              <div className="rounded-xl border border-rose-500/15 bg-rose-500/[0.04] px-4 py-3">
                <p className="text-[12px] text-rose-300/70">{error}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="h-px flex-shrink-0 bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" />
          <DialogFooter className="px-6 py-3.5 gap-2 sm:gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={loading}
              className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-4 py-2 text-[12px] font-medium text-white/50 transition-all duration-200 hover:bg-white/[0.05] hover:text-white/55 disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 rounded-lg bg-teal-500/90 px-5 py-2 text-[12px] font-semibold text-white shadow-sm shadow-teal-500/20 transition-all duration-200 hover:bg-teal-400 hover:shadow-teal-400/30 disabled:opacity-40"
            >
              {loading ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-3.5 w-3.5" />
                  Save Changes
                </>
              )}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
